import { useCallback, useMemo, useRef, useState } from "react";
import { View } from "react-native";
import { GestureDetector } from "react-native-gesture-handler";
import Animated from "react-native-reanimated";
import useMountEffect from "src/hooks/useMountEffect";
import ImageGalleryPager from "./components/ImageGalleryPager";
import useAdjustedDimensions from "./hooks/useAdjustedDimensions";
import useFillPositionLookup from "./hooks/useFillPositionLookup";
import useGetActiveData from "./hooks/useGetActiveData";
import useImageGalleryGestures from "./hooks/useImageGalleryGestures";
import useProcessNewActiveItem from "./hooks/useProcessNewActiveItem";
import { ImageGalleryProps, KeyLookup, PositionLookup } from "./types";

const ImageGallery = <TItem extends unknown>({
	data,
	renderCarouselItem,
	renderMainItem,
	keyExtractor,
	initialKey,
	mainItemDimensions,
	carouselItemDimensions,
	mainGestureComposer,
	carouselGestureComposer,
	mainItemContainerStyle,
	showCarousel = true,
	carouselContainerAnimated,
	carouselItemContainerStyle,
	onStartReached,
	startReachedIndexThreshold = 10,
	onEndReached,
	endReachedIndexThreshold = 10,
	onActiveItem,
	showNavigation,
	mainGesturePanListener,
}: ImageGalleryProps<TItem>) => {
	const curKey = useRef(initialKey);
	const curIndex = useRef(0);

	const keyLookup = useRef<KeyLookup>({});
	const dataRef = useRef(data);
	const fillPositionLookup = useFillPositionLookup({
		curIndex,
		dataRef,
		keyExtractor,
		curKey,
	});
	const positionLookup = useRef<PositionLookup>(
		// biome-ignore lint/correctness/useExhaustiveDependencies: This is a hacky one liner to only run once
		useMemo(() => {
			return fillPositionLookup({}, keyLookup.current);
		}, []),
	);

	const processNewActiveItem = useProcessNewActiveItem({
		curIndex,
		curKey,
		dataRef,
		keyExtractor,
		onActiveItem,
	});

	const getActiveData = useGetActiveData({
		carouselItemDimensions,
		curIndex,
		curKey,
		dataRef,
		keyExtractor,
		startReachedIndexThreshold,
		onStartReached,
		endReachedIndexThreshold,
		onEndReached,
		positionLookup,
		processNewActiveItem,
	});

	const [activeData, setActiveData] = useState<{
		main: { item: TItem; index: number }[];
		carousel: { item: TItem; index: number }[];
	}>(useMemo(getActiveData, []));

	const didRender = useRef(false);
	useMountEffect(() => {
		dataRef.current = data;
		if (didRender.current) {
			fillPositionLookup(positionLookup.current, keyLookup.current);
			setActiveData(getActiveData());
		} else {
			didRender.current = true;
		}
	}, [data, keyExtractor, setActiveData]);

	useMountEffect(() => {
		// This is to ensure that the active item is set on the first render
		processNewActiveItem();
	}, []);

	const setNewKey = useCallback(
		(newKey: string) => {
			curKey.current = newKey;
			setActiveData(getActiveData());
		},
		[getActiveData],
	);

	const { adjustedCarouselItemDimensions, adjustedMainItemDimensions } = useAdjustedDimensions({
		carouselItemDimensions,
		mainItemDimensions,
	});

	const { mainGesture, mainContainerStyle, carouselGesture, carouselContainerStyle, handlePage } =
		useImageGalleryGestures({
			carouselItemDimensions: adjustedCarouselItemDimensions,
			mainItemDimensions: adjustedMainItemDimensions,
			curKey,
			positionLookup,
			setNewKey,
			keyLookup,
			mainGestureComposer,
			carouselGestureComposer,
			mainGesturePanListener,
		});

	const pagerBehavior = useMemo<"FirstItem" | "LastItem" | "MiddleItem" | "None">(() => {
		activeData;
		if (data.length === 0 || data.length === 1) return "None";
		if (curIndex.current === 0) return "FirstItem";
		if (curIndex.current === data.length - 1) return "LastItem";
		return "MiddleItem";
	}, [activeData, data.length]);

	return (
		<Animated.View
			style={[
				{
					width: "100%",
					height: "100%",
					overflow: "hidden",
				},
			]}
		>
			<GestureDetector gesture={mainGesture}>
				<View
					style={[
						mainItemContainerStyle,
						{
							position: "absolute",
							width: adjustedMainItemDimensions.width,
							height: adjustedMainItemDimensions.height,
						},
					]}
				>
					<Animated.View
						style={[
							mainContainerStyle,
							{
								position: "absolute",
								width: "100%",
								height: "100%",
							},
						]}
						pointerEvents="box-none"
					>
						{activeData.main.map(({ item, index }) => {
							const key = keyExtractor({ item });
							const curPosition = positionLookup.current[key];
							if (!curPosition) return null;
							return (
								<View
									key={key}
									style={[
										{
											position: "absolute",
											left:
												curPosition.positionX *
												adjustedMainItemDimensions.width,
											width: adjustedMainItemDimensions.width,
											height: adjustedMainItemDimensions.height,
										},
									]}
									pointerEvents="box-none"
								>
									{renderMainItem({
										item,
										index,
										width: mainItemDimensions.width,
										height: mainItemDimensions.height,
									})}
								</View>
							);
						})}
					</Animated.View>
				</View>
			</GestureDetector>

			{showCarousel && (
				<GestureDetector gesture={carouselGesture}>
					<View
						style={[
							carouselItemContainerStyle,
							{
								position: "absolute",
								width: "100%",
								height: adjustedCarouselItemDimensions.height,
								zIndex: 1,
							},
						]}
					/>
				</GestureDetector>
			)}
			<Animated.View
				{...carouselContainerAnimated}
				style={[
					carouselContainerStyle,
					{
						position: "absolute",
						width: "100%",
						height: "100%",
						left: "50%",
						marginLeft: -adjustedCarouselItemDimensions.width / 2,
					},
				]}
				pointerEvents="box-none"
			>
				{activeData.carousel.map(({ item, index }) => {
					const curKey = keyExtractor({ item });
					const curPosition = positionLookup.current[curKey];
					if (!curPosition) return null;
					return (
						<View
							key={curKey}
							style={[
								carouselItemContainerStyle,
								{
									position: "absolute",
									left:
										curPosition.positionX *
										adjustedCarouselItemDimensions.width,
									width: adjustedCarouselItemDimensions.width,
									height: adjustedCarouselItemDimensions.height,
									opacity: showCarousel ? 1 : 0,
								},
							]}
							pointerEvents="box-none"
						>
							{renderCarouselItem({
								item,
								index,
								width: carouselItemDimensions.width,
								height: carouselItemDimensions.height,
							})}
						</View>
					);
				})}
			</Animated.View>
			<ImageGalleryPager
				behavior={pagerBehavior}
				show={showNavigation}
				mainItemContainerStyle={mainItemContainerStyle}
				mainItemDimensions={mainItemDimensions}
				onPage={handlePage}
			/>
		</Animated.View>
	);
};
export default ImageGallery;
